// frontend/src/pages/CodesPage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  listCodes,
  deleteCode,
  downloadQr,
  downloadZip,
  activateCode,
  deactivateCode,
  bulkCodes,
} from "../api/codes";
import { getFolderTree, notifyFoldersChanged } from "../api/folders";
import { listTags } from "../api/tags";
import CodeTable from "../components/CodeTable";
import CreateCodeModal from "../components/CreateCodeModal";
import EditCodeModal from "../components/EditCodeModal";
import ConfirmDialog from "../components/ConfirmDialog";
import ErrorBanner from "../components/ErrorBanner";
import NewFolderModal from "../components/NewFolderModal";
import NewTagModal from "../components/NewTagModal";
import TagFilterChips from "../components/TagFilterChips";
import BulkActionBar from "../components/BulkActionBar";
import { QR_TYPES } from "../components/QrPayloadFields";

const PAGE_SIZE = 20;
const FILTER_CLS =
  "rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

/** Find a folder node by id within the nested tree (for the active-folder label). */
function findFolder(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const hit = findFolder(n.children, id);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * CodesPage — paginated QR code list with search, folder/tag filters, create,
 * edit, delete, QR download, and bulk actions.
 */
function CodesPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [qrType, setQrType] = useState("");
  const [status, setStatus] = useState("");
  const debounceRef = useRef(null);

  // Folder filter comes from the URL (?folder=<id>) so the sidebar FolderTree
  // and this page share one source of truth.
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get("folder") || "";

  // Folders + tags for the filters, modals, and bulk pickers.
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Bulk selection (array of code ids) + the floating action bar's busy state.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNewTag, setShowNewTag] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  };

  const handleTypeChange = (e) => {
    setQrType(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((t) => t !== tagId)
        : [...prev, tagId],
    );
    setPage(1);
  };

  // Reset to page 1 whenever the folder filter changes.
  useEffect(() => {
    setPage(1);
  }, [folderId]);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listCodes(page, PAGE_SIZE, search, {
        qrType,
        status,
        folderId,
        tagIds: selectedTagIds,
      });
      setData(result);
    } catch {
      setError("Failed to load codes.");
    } finally {
      setLoading(false);
    }
  }, [page, search, qrType, status, folderId, selectedTagIds]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  // Load folders + tags once (best-effort — failures just leave them empty).
  const reloadFoldersTags = useCallback(() => {
    getFolderTree()
      .then(setFolders)
      .catch(() => setFolders([]));
    listTags()
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  useEffect(() => {
    reloadFoldersTags();
  }, [reloadFoldersTags]);

  // ── Create / edit / delete ────────────────────────────────────────────────

  const handleCreated = () => {
    setShowCreate(false);
    setPage(1);
    setSearch("");
    setSearchInput("");
    fetchCodes();
    reloadFoldersTags();
    notifyFoldersChanged();
  };

  const handleUpdated = (updated) => {
    setEditTarget(null);
    setData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === updated.id ? updated : item,
      ),
    }));
    // An edit may have re-foldered the code → keep counts honest.
    reloadFoldersTags();
    notifyFoldersChanged();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCode(deleteTarget.code);
      toast.success(`"${deleteTarget.code}" deleted.`);
      setDeleteTarget(null);
      const newTotal = data.total - 1;
      const maxPage = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
      setPage((p) => Math.min(p, maxPage));
      fetchCodes();
      notifyFoldersChanged();
    } catch {
      toast.error("Failed to delete code.");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const updated = item.is_active
        ? await deactivateCode(item.code)
        : await activateCode(item.code);
      setData((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.id === updated.id ? updated : it)),
      }));
      toast.success(
        `"${updated.code}" ${updated.is_active ? "activated" : "deactivated"}.`,
      );
    } catch (err) {
      // 402 = activating would exceed the plan's active-code cap (e.g. after a
      // downgrade). Surface the reason so the user knows to free a slot/upgrade.
      if (err?.response?.status === 402) {
        toast.error(
          "You've reached your plan's active-code limit. Deactivate another code or upgrade to activate this one.",
        );
      } else {
        toast.error("Failed to update code status.");
      }
    }
  };

  const handleDownloadQr = async (code) => {
    try {
      await downloadQr(code);
    } catch {
      toast.error("Failed to download QR image.");
    }
  };

  // ── Folder / tag creation ─────────────────────────────────────────────────

  const handleFolderCreated = () => {
    setShowNewFolder(false);
    reloadFoldersTags();
    notifyFoldersChanged();
  };

  const handleTagCreated = () => {
    setShowNewTag(false);
    reloadFoldersTags();
  };

  // ── Bulk selection + actions ──────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const pageIds = data.items.map((it) => it.id);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      allOnPageSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : [...new Set([...prev, ...pageIds])],
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const runBulk = async (action, opts = {}) => {
    if (action === "delete") {
      setBulkDeleteOpen(true);
      return;
    }
    setBulkBusy(true);
    try {
      const { affected } = await bulkCodes(selectedIds, action, opts);
      toast.success(`${affected} code${affected === 1 ? "" : "s"} updated.`);
      clearSelection();
      fetchCodes();
      if (action === "move") notifyFoldersChanged();
      reloadFoldersTags();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { affected } = await bulkCodes(selectedIds, "delete");
      toast.success(`${affected} code${affected === 1 ? "" : "s"} deleted.`);
      setBulkDeleteOpen(false);
      clearSelection();
      // Deleting may empty the current page — clamp before refetch.
      const newTotal = Math.max(0, data.total - affected);
      const maxPage = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
      setPage((p) => Math.min(p, maxPage));
      fetchCodes();
      notifyFoldersChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Bulk delete failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  // ── ZIP export ────────────────────────────────────────────────────────────

  const [zipping, setZipping] = useState(false);
  const hasFilters = Boolean(
    search || qrType || status || folderId || selectedTagIds.length,
  );
  const handleDownloadZip = async () => {
    setZipping(true);
    try {
      await downloadZip({ search, qrType, status });
    } catch {
      toast.error("Failed to download ZIP.");
    } finally {
      setZipping(false);
    }
  };

  const activeFolder = folderId ? findFolder(folders, folderId) : null;
  const clearFolder = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("folder");
    setSearchParams(next);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">QR Codes</h1>
          {activeFolder && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
              📁 {activeFolder.name}
              <button
                onClick={clearFolder}
                aria-label="Clear folder filter"
                className="ml-0.5 text-blue-400 hover:text-blue-700"
              >
                ✕
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewFolder(true)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            + New Folder
          </button>
          <button
            onClick={() => setShowNewTag(true)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            + New Tag
          </button>
          <button
            onClick={handleDownloadZip}
            disabled={zipping}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            {zipping
              ? "Zipping…"
              : hasFilters
                ? "↓ Download filtered (ZIP)"
                : "↓ Download all QR images (ZIP)"}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            + New Code
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && <ErrorBanner message={error} onRetry={fetchCodes} />}

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search by name or token…"
          className="flex-1 min-w-[12rem] max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          aria-label="Filter by type"
          value={qrType}
          onChange={handleTypeChange}
          className={FILTER_CLS}
        >
          <option value="">All types</option>
          {QR_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by status"
          value={status}
          onChange={handleStatusChange}
          className={FILTER_CLS}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Tag filter chips (AND semantics) */}
      <TagFilterChips
        tags={tags}
        selectedIds={selectedTagIds}
        onToggle={toggleTag}
        onClear={() => {
          setSelectedTagIds([]);
          setPage(1);
        }}
      />

      {/* Table */}
      <CodeTable
        items={data.items}
        loading={loading}
        onEdit={setEditTarget}
        onDelete={setDeleteTarget}
        onDownloadQr={handleDownloadQr}
        onToggleActive={handleToggleActive}
        selectable
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        allSelected={allOnPageSelected}
      />

      {/* Bulk action bar — appears at ≥ 2 selected codes */}
      {selectedIds.length >= 2 && (
        <BulkActionBar
          count={selectedIds.length}
          folders={folders}
          tags={tags}
          onAction={runBulk}
          onClear={clearSelection}
          busy={bulkBusy}
        />
      )}

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Page {data.page} of {data.pages} ({data.total} total)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page >= data.pages || loading}
              className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateCodeModal
          folders={folders}
          tags={tags}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
      {showNewFolder && (
        <NewFolderModal
          folders={folders}
          onClose={() => setShowNewFolder(false)}
          onCreated={handleFolderCreated}
        />
      )}
      {showNewTag && (
        <NewTagModal
          onClose={() => setShowNewTag(false)}
          onCreated={handleTagCreated}
        />
      )}
      {editTarget && (
        <EditCodeModal
          codeObj={editTarget}
          folders={folders}
          tags={tags}
          onClose={() => setEditTarget(null)}
          onUpdated={handleUpdated}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={`Delete "${deleteTarget.code}"?`}
          message="This code will stop resolving for public scans and disappear from your list. Its scan history is kept, and it can be restored later."
          confirmLabel="Delete"
          dangerous
          loading={deleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {bulkDeleteOpen && (
        <ConfirmDialog
          title={`Delete ${selectedIds.length} codes?`}
          message="These codes will stop resolving for public scans and disappear from your list. Their scan history is kept."
          confirmLabel="Delete all"
          dangerous
          loading={bulkBusy}
          onConfirm={confirmBulkDelete}
          onCancel={() => setBulkDeleteOpen(false)}
        />
      )}
    </div>
  );
}

export default CodesPage;
